// 得意先向け請求書ビュー（中身/TMS）。実物の様式（A4横・多ページ）を 1:1 で再現し、
// `@media print` で刷る（OPEN-DECISIONS #3・ライブラリ追加なし）。
//
// 様式の正本: エースジャパン/請求データ/【請求書】ガットリベロ㈱様_202607.pdf
// 採寸・税計算・ページ分割の根拠は engineering-タクミ/docs/2026-08-28-請求書発行-設計.md。
// 座標はすべて px（96dpi）で、実物PDFの内容ストリームから実測した値そのもの。
// CSS px は定義上 1/96 inch なので、A4横（1122.56×793.76px）に置けば実物と 1:1 になる。
//
// **紙の中はテーマ色を使わない。** 白地 #fff・黒文字 #000・見出し地 #D3D3D3 の直値だけ。
// 取引先が見慣れた帳票の再現であって、ブランド表現ではないため（テーマ切替の対象にしない）。
// 画面プレビューも同じ紙を出す＝WYSIWYG。周囲（戻る・印刷・宛先編集）だけがテーマ色で、
// そこは共通層 styles.css の .btn / .btn--primary / .ct-panel をそのまま使う（2026-08-31 統一）。
// 固定は test_invoice_ui_unify.mjs。
import { buildSheets } from '../data/analytics/chargesheet.js'
import { COLLECTIONS, listDocs, getOne, upsert, invalidateCache } from '../data/collections.js'
import { supabaseReady } from '../supabase.js'
import sampleTrips from './sample.trips.json'
import { allowSample, DEMO_TAG } from '../data/demoData.js'
import { refreshCompany, getCompany } from '../data/companyInfo.js'
import { resolveCustomerInfo, CUSTOMER_FIELDS } from '../data/companyInfo.core.js'
import { invoiceModel, money, LAYOUT, ROWS_FIRST, ROWS_REST } from '../data/invoice.core.js'

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]))
const V = { el: null, ctx: {}, month: '', key: '', model: null, group: null, online: false, editing: false, saving: '', isSample: false }

// ---- 採寸（px・実物PDF実測。設計メモ §2 の表と同じ値） ----
const L = LAYOUT
const COL = L.cols
const TAB_W = COL.reduce((a, b) => a + b, 0)          // 1029.5
const HEAD_H = L.headH                                 // 38.5
const ROW_H = L.rowH                                   // 30.21

function styleOnce() {
  if (document.getElementById('inv-style')) return
  const s = document.createElement('style'); s.id = 'inv-style'
  s.textContent = `
  /* ---------- 紙の外（操作UI）。共通層 styles.css の部品をそのまま使う ----------
     戻る/印刷/宛先を編集/保存 = .btn・.btn--primary、宛先編集パネル = .ct-panel。
     ここでボタンやパネルを作り直さない（二重定義になり、主導線の色が請求書だけ違う事故になる）。
     残しているのはこのビューにしか無いもの（紙の並べ方・宛先の入力欄・注記・バッジ）だけ。 */
  .inv{display:flex;flex-direction:column;gap:12px}
  .inv-bar{display:flex;align-items:center;gap:10px;flex-wrap:wrap}
  /* 宛先編集パネルの見出し・操作行。dashboard の「自社情報（請求書）」パネルと同じ流儀にそろえる
     （同じ請求書の宛先／自社側を直す画面なので、見た目が違う理由が無い） */
  .inv .ct-panel h3{margin:0 0 10px;font-size:12.5px;display:flex;align-items:center;gap:9px}
  .inv-acts{display:flex;gap:9px;align-items:center;margin-top:11px;flex-wrap:wrap}
  .inv-ef{display:grid;grid-template-columns:repeat(auto-fill,minmax(210px,1fr));gap:10px}
  .inv-ef label{display:flex;flex-direction:column;gap:4px;font-size:10.5px;color:var(--text)}
  .inv-ef input{font-family:inherit;font-size:13px;padding:6px 8px;border:1px solid var(--e1);border-radius:7px;background:var(--head);color:var(--text)}
  /* デモ明示バッジ・保存済みバッジ・注記は各ビューが同じ形で持つのが現行の作法
     （文言は demoData.core.js の DEMO_TAG が唯一の正）。billing/dashboard と同一の宣言にそろえてある */
  .inv-demo{font-size:11px;color:var(--signal);background:transparent;border:1px solid var(--accent);border-radius:999px;padding:3px 10px}
  .inv-savedok{font-size:10.5px;color:var(--pos);background:var(--head);border:1px solid var(--pos);border-radius:999px;padding:2px 9px}
  .inv-note{font-size:11px;color:var(--muted);line-height:1.6}
  /* 未登録の注意書き。accent 枠 × signal 文字の帯は ingest の .ing-banner と同じ形 */
  .inv-warn{font-size:12px;color:var(--signal);background:var(--card);border:1px solid var(--accent);border-radius:9px;padding:9px 12px}
  .inv-sheet{display:flex;flex-direction:column;gap:14px;align-items:flex-start;overflow:auto;padding-bottom:6px}

  /* ---------- ここから下が「紙」。テーマ変数は一切使わない（白地・黒文字が正） ---------- */
  .inv-paper{position:relative;box-sizing:border-box;width:${L.pageW}px;height:${L.pageH}px;background:#fff;color:#000;
    font-family:"MS Mincho","ＭＳ 明朝","MS 明朝","Hiragino Mincho ProN","Yu Mincho",serif;
    font-size:12.96px;line-height:1.1;overflow:hidden;flex:0 0 auto;
    box-shadow:0 0 0 1px rgba(0,0,0,.25)}
  .inv-paper *{box-sizing:border-box}
  .inv-abs{position:absolute;white-space:pre;color:#000}
  .inv-title{font-size:20.96px;font-weight:700;border-bottom:1px solid #000;padding:0;line-height:20.9px}
  .inv-ul{border-bottom:1px solid #000}
  .inv-box{position:absolute;border:1px solid #000;border-radius:6px}
  .inv-to{position:absolute;display:flex;align-items:baseline;overflow:hidden;border-bottom:1px solid #000;line-height:15px}
  .inv-to .nm{flex:1 1 auto;min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;padding-left:7.5px}
  .inv-to .on{flex:0 0 auto;padding-left:8px}
  .inv-no{position:absolute;display:flex;justify-content:space-between;border-bottom:1px solid #000;line-height:12.9px}

  /* 罫線は border-collapse を使わない。collapse だと指定した高さに罫線1px が上乗せされ、
     13行・19行と積むと数十px ずれる（実測。1ページ目の枠下端が 753px になった）。
     separate + border-spacing:0 ＋ セルは右下だけ・表は左上だけに罫を持たせると
     セルの border-box 寸法＝採寸値そのものになり、行を何行積んでもズレない。
     表を採寸位置から左上に1pxずらして置くことで、外枠の線が採寸した座標に乗る。 */
  table.inv-sum,table.inv-tbl{position:absolute;border-collapse:separate;border-spacing:0;table-layout:fixed;
    border-left:1px solid #000;border-top:1px solid #000}
  .inv-sum th,.inv-sum td,.inv-tbl th,.inv-tbl td{border-right:1px solid #000;border-bottom:1px solid #000;font-weight:400}

  table.inv-sum{font-size:16px;line-height:1}
  .inv-sum th,.inv-sum td{padding:0}
  .inv-sum th{height:20.7px;background:#D3D3D3;text-align:center}
  /* 値行は実物どおり 16px。16px の文字を line-height:1 で入れると罫線1px の分だけ行が伸びるため 15px にする */
  .inv-sum td{height:16px;line-height:15px;text-align:right;padding-right:2px;font-variant-numeric:tabular-nums}

  table.inv-tbl{font-size:12.96px;line-height:1.1}
  .inv-tbl th,.inv-tbl td{padding:0 2px;overflow:hidden;white-space:nowrap;vertical-align:middle}
  .inv-tbl th{height:${HEAD_H}px;background:#D3D3D3;text-align:center;padding:0}
  .inv-tbl tr.r td{height:${ROW_H}px}
  .inv-tbl td.c{text-align:center}
  .inv-tbl td.n{text-align:right;padding-right:1.3px;font-variant-numeric:tabular-nums}
  .inv-tbl td.l{text-align:left;padding-left:2.1px}
  .inv-tbl td.wt{padding:0;text-align:right;font-variant-numeric:tabular-nums}
  .inv-tbl td.wt i{display:block;height:${(ROW_H - 1) / 2}px;line-height:${(ROW_H - 1) / 2}px;font-style:normal;padding-right:1.3px}
  .inv-tbl td.wt i.up{border-bottom:1px solid #000}
  .inv-tbl th.wt{padding:0}
  .inv-tbl th.wt i{display:block;height:${(HEAD_H - 1) / 2}px;line-height:${(HEAD_H - 1) / 2}px;font-style:normal}
  .inv-tbl th.wt i.up{border-bottom:1px solid #000}
  /* 合計行のラベルは品名列いっぱいまで使う（【10％】消費税額 が最長） */
  .inv-tbl td.tl{text-align:center;padding:0}
  .inv-tbl tr.b td{font-weight:700}

  @media print {
    @page { size: A4 landscape; margin: 0 }
    html,body{background:#fff !important}
    .inv-bar,.inv .ct-panel,.inv-note,.inv-warn,.inv-demo{display:none !important}
    .inv,.inv-sheet{display:block;gap:0;padding:0;overflow:visible}
    /* 紙の箱は A4横の刷り面ちょうど（297×210mm）にする。画面用の採寸 1122.56×793.76px は
       96dpi の A4横 1122.5197×793.7008px を 0.04/0.06px **超えている**ため、そのまま刷ると
       刷り面からはみ出す。中身は絶対配置で最も外側でも x≤1077.8 / y≤745.0 なので1文字も欠けない。
       break-inside:avoid = 1枚の紙は絶対に2ページに割らない。紙は overflow:hidden の
       monolithic box で分割できず、刷り面に収まらないと**白紙が1枚増える**（2026-08-31 実機で発生）。
       ブラウザの印刷余白が「なし」以外だと刷り面が縮むので、この宣言が最後の砦になる。 */
    .inv-paper{box-shadow:none;margin:0;width:297mm;height:210mm;
      break-inside:avoid;page-break-inside:avoid}
    /* 改ページは「2枚目以降の紙の**前**」で起こす。break-after:page ＋ :last-child 打ち消しは、
       紙のあとに要素が1つ増えただけで末尾に白紙が出る（display:none の要素も :last-child の
       判定には効いてしまう）。隣接セレクタなら最後の紙のあとに改ページが残らない。 */
    .inv-paper + .inv-paper{break-before:page;page-break-before:always}
  }
  `
  document.head.appendChild(s)
}

// ---------------------------------------------------------------- 紙の描画

const at = (x, y, extra = '') => `left:${x}px;top:${y}px;${extra}`

// 明細表（1ページ分）。rows は invoice.core の paginateRows が返した1ページ分
function tableHtml(rows, first) {
  const top = first ? L.firstTop : L.restTop
  const cap = first ? ROWS_FIRST : ROWS_REST
  const cols = COL.map((w) => `<col style="width:${w}px">`).join('')
  const th = `<tr>
    <th>月日</th><th>発地名</th><th>着地名</th><th>車番</th><th>品名</th><th>数量</th><th>単位</th><th>ｔ数</th><th>単価</th><th>基本運賃</th>
    <th class="wt"><i class="up">割増</i><i>通行料</i></th><th>備考</th></tr>`
  const body = rows.map((r) => {
    if (r.type === 'detail') {
      return `<tr class="r"><td class="c">${esc(r.day)}</td><td class="l">${esc(r.a)}</td><td class="l">${esc(r.b)}</td>
        <td class="c">${esc(r.sha)}</td><td class="l">${esc(r.hin)}</td><td class="n">${esc(r.qty)}</td><td class="c">${esc(r.unit)}</td>
        <td class="n">${esc(r.ton)}</td><td class="n">${esc(r.price)}</td><td class="n">${money(r.amt)}</td>
        <td class="wt"><i class="up">${money(r.wari)}</i><i>${money(r.toll)}</i></td><td class="l">${esc(r.biko)}</td></tr>`
    }
    if (r.type === 'total') {
      const v = r.paren ? `(${money(r.amount)})` : money(r.amount)
      return `<tr class="r${r.bold ? ' b' : ''}"><td></td><td></td><td></td><td></td><td class="tl">${esc(r.label)}</td>
        <td></td><td></td><td></td><td></td><td class="n">${v}</td>
        <td class="wt"><i class="up"></i><i></i></td><td></td></tr>`
    }
    return `<tr class="r"><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td></td><td class="wt"><i class="up"></i><i></i></td><td></td></tr>`
  }).join('')
  // 余りは1行にまとめる＝横罫を描かず、縦罫だけを枠下端まで伸ばす
  const restH = (cap - rows.length) * ROW_H
  const fill = restH > 0.5 ? `<tr class="fill"><td style="height:${restH}px"></td>${COL.slice(1).map(() => '<td></td>').join('')}</tr>` : ''
  return `<table class="inv-tbl" style="${at(L.tableLeft - 1, top - 1, `width:${TAB_W + 1}px`)}"><colgroup>${cols}</colgroup>
    <thead>${th}</thead><tbody>${body}${fill}</tbody></table>`
}

// 宛名ブロック（1ページ目の枠内 / 2ページ目以降のヘッダ）。御中は右詰め（実物で run の右端が常に431.4）
const toHtml = (name, top) => `<div class="inv-to" style="${at(100.5, top, 'width:331px;height:15px')}"><span class="nm">${esc(name)}</span><span class="on">御中</span></div>`
const noHtml = (n, top) => `<div class="inv-no" style="${at(984.1, top, 'width:52.4px;height:12.9px')}"><span>No.</span><span>${n}</span></div>`

function page1Head(m) {
  const c = m.company, k = m.customer
  const S = m.summary
  const cell = (v) => `<td>${money(v)}</td>`
  return `
    <div class="inv-abs inv-title" style="${at(540.3, 38.0)}">請求書</div>
    <div class="inv-abs" style="${at(484.1, 69.9, 'font-size:16px;line-height:16px')}">${esc(m.dateJa)}</div>
    ${noHtml(m.pageNo || 1, 54.0)}

    <div class="inv-box" style="${at(87.0, 64.3, 'width:372.5px;height:135.2px')}"></div>
    <div class="inv-abs" style="${at(100.5, 70.0, 'font-size:15.04px;line-height:15px')}">${esc(k.zip)}</div>
    <div class="inv-abs" style="${at(100.5, 101.9, 'font-size:15.04px;line-height:15px')}">${esc(k.addr1)}</div>
    <div class="inv-abs" style="${at(100.5, 118.0, 'font-size:15.04px;line-height:15px')}">${esc(k.addr2)}</div>
    <div style="font-size:15.04px">${toHtml(k.name, 166.0)}</div>
    <div class="inv-abs" style="${at(100.5, 181.9, 'font-size:15.04px;line-height:15px')}">${k.tel ? 'TEL ' + esc(k.tel) : ''}</div>
    <div class="inv-abs" style="${at(250.4, 181.9, 'font-size:15.04px;line-height:15px')}">${k.fax ? 'FAX ' + esc(k.fax) : ''}</div>

    <div class="inv-abs" style="${at(846.6, 85.9, 'font-size:15.04px;line-height:15px')}">登録番号</div>
    <div class="inv-abs" style="${at(920.4, 85.9, 'font-size:15.04px;line-height:15px')}">${esc(c.tnum)}</div>
    <div class="inv-abs" style="${at(760.5, 118.0, 'font-size:15.04px;line-height:15px')}">${esc(c.name)}</div>
    <div class="inv-abs" style="${at(763.8, 133.9, 'font-size:15.04px;line-height:15px')}">〒 ${esc(c.zip)}</div>
    <div class="inv-abs" style="${at(760.5, 149.9, 'font-size:15.04px;line-height:15px')}">${esc(c.address)}</div>
    <div class="inv-abs" style="${at(820.5, 166.0, 'font-size:15.04px;line-height:15px')}">TEL ${esc(c.tel)}</div>
    <div class="inv-abs" style="${at(820.5, 181.9, 'font-size:15.04px;line-height:15px')}">FAX ${esc(c.fax)}</div>

    <table class="inv-sum" style="${at(97.4, 208.1, 'width:590.4px')}">
      <colgroup><col style="width:120px"><col style="width:109.8px"><col style="width:130px"><col style="width:109.8px"><col style="width:119.8px"></colgroup>
      <thead><tr><th>合計金額</th><th>通行料</th><th>課税対象額</th><th>消費税額</th><th>合計請求額</th></tr></thead>
      <tbody><tr>${cell(S.fare)}${cell(S.toll)}${cell(S.taxable)}${cell(S.tax)}${cell(S.total)}</tr></tbody></table>

    <div class="inv-abs" style="${at(600.5, 262.0, 'font-size:15.04px;line-height:16px')}">${esc(c.bank1)}</div>
    <div class="inv-abs" style="${at(600.5, 278.0, 'font-size:15.04px;line-height:16px')}">${esc(c.bank2)}</div>`
}

// 2ページ目以降: 「得意先名 御中」＋ No.n ＋ 表ヘッダのみ（サマリ帯・振込先・日付は出さない）
const pageNHead = (m, n) => `<div style="font-size:15.04px">${toHtml(m.customer.name, 82.4)}</div>${noHtml(n, 82.5)}`

function paperHtml(m) {
  return m.pages.map((rows, i) => `<div class="inv-paper">
    ${i === 0 ? page1Head({ ...m, pageNo: 1 }) : pageNHead(m, i + 1)}
    ${tableHtml(rows, i === 0)}
  </div>`).join('')
}

// ---------------------------------------------------------------- 画面

function paint() {
  const m = V.model
  const miss = m.missing
  const warn = miss.length
    ? `<span class="inv-warn">この得意先は ${esc(miss.join('・'))} が未登録です。紙は空欄のまま刷れますが、宛先を入れるには「宛先を編集」から登録してください。</span>`
    : ''
  V.el.innerHTML = `
    <section class="inv">
      <div class="inv-bar">
        <a class="btn" href="#/billing">← 請求・支払</a>
        <button type="button" class="btn btn--primary" id="inv-print">印刷 / PDFで保存</button>
        <button type="button" class="btn" id="inv-edit-t">宛先を編集</button>
        ${V.isSample ? `<span class="inv-demo">${DEMO_TAG}</span>` : ''}
        ${warn}
      </div>
      ${V.editing ? editHtml() : ''}
      <div class="inv-sheet">${paperHtml(m)}</div>
      <div class="inv-note">
        A4横・${m.pages.length}ページ（1ページ目 ${ROWS_FIRST}行／2ページ目以降 ${ROWS_REST}行）。実物の様式に合わせています。
        <b>通行料は内税</b>: サマリ帯の「通行料」は税抜 ¥${money(m.summary.toll)}、末尾内訳の「通行料」は税込 ¥${money(m.tax.tollGross)}。
        課税対象額＝基本運賃計＋税抜通行料。消費税＝課税対象額×10%（四捨五入）。
        自社名・登録番号・住所・振込先はダッシュボードの「自社情報」で変更できます。
        印刷は用紙 A4・向き 横・余白なし・「背景のグラフィック」ONで実物と同じ紙になります。
      </div>
    </section>`
  V.el.querySelector('#inv-print').addEventListener('click', () => window.print())
  V.el.querySelector('#inv-edit-t').addEventListener('click', () => { V.editing = !V.editing; V.saving = ''; paint() })
  if (V.editing) wireEdit()
}

// 宛先（得意先マスタ）の編集。「得意先・単価」画面はプロトタイプ埋め込みで実データを持たないため、
// 未登録に気づく場所＝この紙の上で直せるようにした（印刷には出ない）。設計メモ §6
function editHtml() {
  const k = V.model.customer
  const dis = (V.online && k.docId) ? '' : ' disabled'
  const f = ([id, label]) => `<label>${esc(label)}<input id="ic-${id}" type="text" value="${esc(k[id] || '')}"${dis}></label>`
  return `<div class="ct-panel">
    <h3>宛先（得意先マスタ）${V.saving ? `<span class="inv-savedok">${esc(V.saving)}</span>` : ''}</h3>
    <div class="inv-ef">${CUSTOMER_FIELDS.map(f).join('')}</div>
    <div class="inv-acts">
      <button type="button" class="btn btn--primary" id="ic-save"${dis}>保存して紙に反映</button>
      <span class="inv-note">${k.docId ? `customers / ${esc(k.docId)} に保存します。` : 'この得意先は取込時にコードが付いていないためマスタを特定できません（取込の名寄せが要ります）。'}${V.online ? '' : ' 保存は本番のみ。'}</span>
    </div>
  </div>`
}

function wireEdit() {
  const btn = V.el.querySelector('#ic-save')
  if (!btn) return
  btn.addEventListener('click', async () => {
    const patch = {}
    for (const [id] of CUSTOMER_FIELDS) { const e2 = V.el.querySelector('#ic-' + id); if (e2) patch[id] = e2.value.trim() }
    btn.disabled = true
    try {
      await upsert(COLLECTIONS.customers, V.model.customer.docId, patch)
      invalidateCache(COLLECTIONS.customers)
      V.saving = '保存しました'
      V.model = build(V.group, resolveCustomerInfo(patch))
    } catch (e) { V.saving = '保存失敗（権限/接続）' }
    paint()
  })
}

const build = (g, info) => invoiceModel(g, V.month, getCompany(), info)

function paintMissing(msg) {
  V.el.innerHTML = `<section class="inv"><div class="inv-bar"><a class="btn" href="#/billing">← 請求・支払</a></div>
    <div class="ct-state is-empty"><b class="ct-state__t">請求書を作れませんでした</b><span>${esc(msg)}</span></div></section>`
}

export async function renderInvoice(el, ctx = {}) {
  styleOnce()
  V.el = el; V.ctx = ctx; V.editing = false; V.saving = ''
  V.online = !ctx.demo && supabaseReady
  const q = new URLSearchParams((location.hash.split('?')[1] || ''))
  V.month = q.get('m') || ''
  V.key = q.get('k') || ''
  el.innerHTML = '<div class="ct-state is-loading"><b class="ct-state__t">請求書を組み立てています…</b><span>選択月の運行を集計しています</span></div>'

  let trips = sampleTrips
  if (V.online) {
    try { const t = await listDocs(COLLECTIONS.trips); if (t && t.length) trips = t; else if (!allowSample(V.online)) trips = [] }
    catch { V.online = false }
  }
  V.isSample = trips === sampleTrips
  try { await refreshCompany(false) } catch (e) { /* 既定値で紙は成立する */ }

  const sheets = buildSheets(trips)
  const arr = (sheets[V.month] && sheets[V.month].seikyu) || []
  const g = arr.find((x) => x.key === V.key) || (arr.length === 1 ? arr[0] : null)
  if (!g) { paintMissing(`${esc(V.month || '選択月')} にこの得意先のご請求がありません。請求・支払の一覧から選び直してください。`); return }
  V.group = g

  let info = resolveCustomerInfo(null)
  const docId = (g.code != null) ? String(g.code).padStart(6, '0') + (g.branchNo != null ? `-${g.branchNo}` : '') : ''
  if (V.online && docId) { try { info = resolveCustomerInfo(await getOne(COLLECTIONS.customers, docId)) } catch (e) { /* 未登録は空欄で刷る */ } }
  V.model = build(g, info)
  paint()
}
